import { Button, Input, Option, Select, Spinner } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useLabelsContext } from '../contexts/LabelsContext'
import { AgamiLibraryFacet, AgamiLibraryResponse } from '../types/agami-library'
import {
  AgamiLibraryQuery,
  AgamiLibrarySort,
  AgamiLibraryStatus,
} from '../utils/helpers/agami-library.helpers'

interface AgamiLibraryControlsProps {
  query: AgamiLibraryQuery
  statusFacets: AgamiLibraryResponse['facets']['status']
  activationFacets: AgamiLibraryFacet[]
  mediaFacets: AgamiLibraryFacet[]
  languageFacets: AgamiLibraryFacet[]
  isLoading: boolean
  onChange: (changes: Partial<AgamiLibraryQuery>) => void
  onReset: () => void
}

const ALL = 'all'
const STATUSES: AgamiLibraryStatus[] = ['all', 'published', 'drafts']
const SORTS: AgamiLibrarySort[] = ['default', 'name', 'lastUpdate']

export const AgamiLibraryControls: React.FC<AgamiLibraryControlsProps> = ({
  query,
  statusFacets,
  activationFacets,
  mediaFacets,
  languageFacets,
  isLoading,
  onChange,
  onReset,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { getSpotTypeLabel } = useLabelsContext()
  const [search, setSearch] = useState(query.search)

  useEffect(() => setSearch(query.search), [query.search])

  useEffect(() => {
    const normalizedSearch = search.trim()
    if (normalizedSearch === query.search) return

    const timeoutId = window.setTimeout(() => onChange({ search: normalizedSearch }), 350)
    return () => window.clearTimeout(timeoutId)
  }, [onChange, query.search, search])

  const hasFilters = Boolean(
    query.status !== 'all' || query.search || query.activationType || query.mediaType || query.language
  )
  const selectProps = (active: boolean) => ({
    size: 'md' as const,
    menuProps: { className: 'dark:bg-primary' },
    ...(!active && darkMode ? { color: 'blue-gray' as const } : {}),
    className: active
      ? '!border-primary !border-t-transparent bg-primary/10 transition-colors duration-150 dark:!border-accent-blue-500 dark:!border-t-transparent dark:bg-accent-blue-500/15'
      : '',
    labelProps: {
      className: active
        ? '!text-primary before:!border-primary after:!border-primary peer-focus:!text-primary dark:!text-accent-blue-300 dark:before:!border-accent-blue-500 dark:after:!border-accent-blue-500 dark:peer-focus:!text-accent-blue-300'
        : 'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
    },
  })

  const optionLabel = (key: string, count: number) => `${t(key)} · ${count}`

  return (
    <div className="flex flex-col gap-4 mb-5 md:flex-row md:flex-wrap md:items-center">
      <div className="w-full md:w-64">
        <Input
          label={t('agamiLibrary.search.label')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          crossOrigin={undefined}
          name="agami-library-search"
          autoComplete="off"
          spellCheck={false}
          className="dark:text-white"
          labelProps={{
            className:
              'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
          }}
          {...(darkMode && { color: 'white' as colors })}
          data-testid="agami-library-search"
        />
      </div>

      <div className="w-full md:w-48">
        <Select
          {...selectProps(query.status !== 'all')}
          label={t('agamiLibrary.filters.status')}
          value={query.status}
          data-testid="agami-library-status-filter"
          onChange={(value) => value && onChange({ status: value as AgamiLibraryStatus })}
        >
          {STATUSES.map((status) => (
            <Option key={status} value={status} data-testid={`agami-library-status-option-${status}`}>
              <span className="text-primary dark:text-white">
                {optionLabel(`agamiLibrary.statusFilter.${status}`, statusFacets[status])}
              </span>
            </Option>
          ))}
        </Select>
      </div>

      <div className="w-full md:w-48">
        <Select
          {...selectProps(query.activationType !== null)}
          label={t('agamiLibrary.filters.activation')}
          value={query.activationType === null ? ALL : String(query.activationType)}
          data-testid="agami-library-activation-filter"
          onChange={(value) => onChange({ activationType: !value || value === ALL ? null : Number(value) })}
        >
          {[
            <Option key={ALL} value={ALL}>
              <span className="text-primary dark:text-white">{t('agamiLibrary.filters.all')}</span>
            </Option>,
            ...activationFacets.map((facet) => (
              <Option key={facet.value} value={String(facet.value)}>
                <span className="text-primary dark:text-white capitalize">
                  {getSpotTypeLabel(Number(facet.value)) ?? facet.value} · {facet.count}
                </span>
              </Option>
            )),
          ]}
        </Select>
      </div>

      <div className="w-full md:w-48">
        <Select
          {...selectProps(query.mediaType !== null)}
          label={t('agamiLibrary.filters.media')}
          value={query.mediaType === null ? ALL : String(query.mediaType)}
          data-testid="agami-library-media-filter"
          onChange={(value) => onChange({ mediaType: !value || value === ALL ? null : Number(value) })}
        >
          {[
            <Option key={ALL} value={ALL}>
              <span className="text-primary dark:text-white">{t('agamiLibrary.filters.all')}</span>
            </Option>,
            ...mediaFacets.map((facet) => (
              <Option
                key={facet.value}
                value={String(facet.value)}
                data-testid={`agami-library-media-option-${facet.value}`}
              >
                <span className="text-primary dark:text-white">
                  {optionLabel(`agamiLibrary.media.${facet.value}`, facet.count)}
                </span>
              </Option>
            )),
          ]}
        </Select>
      </div>

      <div className="w-full md:w-48">
        <Select
          {...selectProps(query.language !== null)}
          label={t('agamiLibrary.filters.language')}
          value={query.language ?? ALL}
          data-testid="agami-library-language-filter"
          onChange={(value) => onChange({ language: !value || value === ALL ? null : value })}
        >
          {[
            <Option key={ALL} value={ALL}>
              <span className="text-primary dark:text-white">{t('agamiLibrary.filters.all')}</span>
            </Option>,
            ...languageFacets.map((facet) => (
              <Option key={facet.value} value={String(facet.value)}>
                <span className="text-primary dark:text-white">
                  {facet.value === 'undeclared'
                    ? optionLabel('agamiLibrary.languages.undeclared', facet.count)
                    : `${String(facet.value).toUpperCase()} · ${facet.count}`}
                </span>
              </Option>
            )),
          ]}
        </Select>
      </div>

      <div className="w-full md:w-48">
        <Select
          {...selectProps(query.sort !== 'default')}
          label={t('agamiLibrary.sort.label')}
          value={query.sort}
          data-testid="agami-library-sort"
          onChange={(value) => value && onChange({ sort: value as AgamiLibrarySort })}
        >
          {SORTS.map((sort) => (
            <Option key={sort} value={sort}>
              <span className="text-primary dark:text-white">{t(`agamiLibrary.sort.${sort}`)}</span>
            </Option>
          ))}
        </Select>
      </div>

      {hasFilters && (
        <Button
          size="sm"
          variant="text"
          className="whitespace-nowrap underline !text-primary dark:!text-accent"
          onClick={onReset}
          data-testid="agami-library-reset"
        >
          {t('agamiLibrary.filters.reset')}
        </Button>
      )}
      {isLoading && <Spinner className="h-5 w-5 shrink-0" />}
    </div>
  )
}
